import React, { createContext, useContext, ReactNode, useMemo, useEffect, useRef } from 'react'
import opentelemetry, { Attributes, SpanStatusCode, Tracer } from '@opentelemetry/api'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { defaultResource, resourceFromAttributes } from '@opentelemetry/resources'
import type { Resource } from '@opentelemetry/resources'
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'

import { useRetorikStore } from './RetorikContext'
import { useDirectlineStore } from './DirectlineContext'
import { useActivityStore } from './ActivityContext'

import { telemetryAddress, nonResponseInducingEvents } from '../../models/constants'
import { TraceType } from '../../models/enums'
import { version } from '../../../package.json'
import { RetorikActivity } from '../../models/activityTypes'

export type TelemetryProviderProps = {
  enabled: boolean
  children?: ReactNode
}

interface TraceData {
  activityId?: string
  userActivity?: ActivityData
  text?: string
}

interface TelemetryContextType {
  enabled: boolean
  tracer: Tracer | undefined
  sendTrace: (type: TraceType, data: TraceData) => void
  sendErrorTrace: (x: Error) => void
}

interface ActivityData {
  id: string
  time: number
}

const TelemetryContextDefaultValues: TelemetryContextType = {
  enabled: true,
  tracer: undefined,
  sendTrace: () => {},
  sendErrorTrace: () => {}
}

export const TelemetryContext = createContext<TelemetryContextType>(TelemetryContextDefaultValues)

export function useTelemetry(): TelemetryContextType {
  return useContext(TelemetryContext)
}

export function TelemetryProvider({ enabled, children }: TelemetryProviderProps): JSX.Element {
  const userId = useDirectlineStore((state) => state.userId)
  const conversationId = useActivityStore((state) => state.conversationId)
  const addressData = useRetorikStore((state) => state.addressData)
  const lastActivity = useActivityStore((state) => state.lastActivity)
  const botStreamingActivities = useActivityStore((state) => state.botStreamingActivities)

  const lastUserActivityRef = useRef<ActivityData | null>(null)

  const tracer = useMemo<Tracer | undefined>(() => {
    if (enabled && userId && conversationId) {
      const exporter = new OTLPTraceExporter({
        url: telemetryAddress,
        headers: {},
        concurrencyLimit: 1
      })

      let serviceNamespace = ''
      switch (addressData.prefix) {
        case 'dev':
          serviceNamespace = 'retorik-dev'
          break
        case 'staging':
        case 'preview':
          serviceNamespace = 'retorik-preview'
          break
        case 'experimental':
          serviceNamespace = 'retorik-experimental'
          break
        default:
          serviceNamespace = 'retorik-prod'
          break
      }

      // Create resource with custom parameters used globally in uptrace
      const resource: Resource = defaultResource().merge(
        resourceFromAttributes({
          [ATTR_SERVICE_NAME]: 'retorik-framework',
          ['davi.retorik.activity.conversation.id']: conversationId,
          ['service_namespace']: serviceNamespace,
          ['retorik_framework_version']: version,
          ['user_id']: userId
        })
      )

      const processor = new BatchSpanProcessor(exporter)
      const provider = new WebTracerProvider({
        resource: resource,
        spanProcessors: [processor]
      })
      provider.register()

      // Add common attributes to each span for better tracing
      registerInstrumentations({
        instrumentations: [new FetchInstrumentation(), new DocumentLoadInstrumentation()]
      })

      return opentelemetry.trace.getTracer('retorik-framework')
    }

    return undefined
  }, [enabled, userId, conversationId, addressData, version])

  useEffect(() => {
    if (lastActivity) {
      lastActivity.from.role === 'user' ? handleUserActivity(lastActivity) : handleBotActivity(lastActivity)
    }
  }, [lastActivity])

  useEffect(() => {
    if (lastUserActivityRef.current) {
      const temp = botStreamingActivities.get(lastUserActivityRef.current.id)
      temp?.length === 1 && handleBotActivity(temp[0])
    }
  }, [botStreamingActivities])

  const handleUserActivity = (activity: RetorikActivity): void => {
    // Only handle message activites and event activities waiting for an answer, nothing for typing ones
    if (activity.type === 'message' || (activity.type === 'event' && !nonResponseInducingEvents.find((eventName) => activity.name?.toLowerCase().includes(eventName)))) {
      lastUserActivityRef.current = { id: activity.id, time: Date.now() }
    }
  }

  const handleBotActivity = (activity: RetorikActivity): void => {
    // Do nothing for bring-up messages and non-streaming events
    if (
      lastUserActivityRef.current &&
      (activity.type === 'message' || activity.name?.toLocaleLowerCase() === 'davi.streammessageactivity') &&
      activity.label?.toLowerCase() !== 'davi.bringupmessage' &&
      activity.replyToId === lastUserActivityRef.current.id
    ) {
      sendTrace(TraceType.message, {
        activityId: activity.id,
        userActivity: lastUserActivityRef.current
      })
      lastUserActivityRef.current = null
    }
  }

  const sendTrace = (type: TraceType, data?: TraceData): void => {
    if (tracer && type) {
      const parameters: Attributes = data?.activityId
        ? {
            'davi.retorik.activity.id': data.activityId
          }
        : {}

      let startTime= 0
      switch (type) {
        case TraceType.message:
          if (data?.userActivity) {
            startTime = data?.userActivity.time
          }
          break
        case TraceType.utteranceStart:
        case TraceType.utteranceEnd:
          parameters.text = data?.text
          break
      }

      const span = tracer.startSpan(type, startTime ? { startTime } : undefined)
      span.setStatus({
        code: SpanStatusCode.OK
      })
      span.setAttributes(parameters)
      span.end()
    }
  }

  const sendErrorTrace = (error: Error): void => {
    if (tracer) {
      const span = tracer.startSpan('Error')

      span.recordException(error)
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: `${error.message}`
      })

      span.end()
    }
  }

  const value = useMemo(
    () => ({
      enabled,
      tracer,
      sendTrace,
      sendErrorTrace
    }),
    [enabled, tracer]
  )

  return <TelemetryContext.Provider value={value}>{children}</TelemetryContext.Provider>
}
